import fabric from '@/lib/fabric';

export type GradientKind = 'linear' | 'radial';

export interface GradientColors {
  from: string;
  to: string;
}

const FILTER_TYPE = 'CraftaraGradientOverlay';

type GradientFilterInstance = fabric.IBaseFilter & {
  type?: string;
  from?: string;
  to?: string;
  kind?: GradientKind;
  strength?: number;
};

let filterRegistered = false;

function ensureGradientFilter() {
  if (filterRegistered) return;
  const filters = fabric.Image?.filters as unknown as
    | Record<string, unknown>
    | undefined;
  if (!filters || !fabric.util?.createClass) return;

  const BaseFilter = filters.BaseFilter as new (
    options?: Record<string, unknown>
  ) => fabric.IBaseFilter;

  const CraftaraGradientOverlay = fabric.util.createClass(BaseFilter, {
    type: FILTER_TYPE,
    from: '#152033',
    to: '#0d7377',
    kind: 'linear',
    strength: 0.85,

    initialize(options: Record<string, unknown> = {}) {
      this.callSuper('initialize', options);
      this.from = (options.from as string) || this.from;
      this.to = (options.to as string) || this.to;
      this.kind = (options.kind as GradientKind) || this.kind;
      this.strength =
        typeof options.strength === 'number' ? options.strength : this.strength;
    },

    applyTo2d(options: { imageData: ImageData }) {
      const { data, width, height } = options.imageData;
      const surface = document.createElement('canvas');
      surface.width = width;
      surface.height = height;
      const ctx = surface.getContext('2d');
      if (!ctx) return;

      let fill: CanvasGradient;
      if (this.kind === 'radial') {
        const cx = width / 2;
        const cy = height / 2;
        fill = ctx.createRadialGradient(
          cx,
          cy,
          0,
          cx,
          cy,
          Math.max(width, height) / 2
        );
      } else {
        fill = ctx.createLinearGradient(0, 0, width, height);
      }
      fill.addColorStop(0, this.from || '#000000');
      fill.addColorStop(1, this.to || '#ffffff');
      ctx.fillStyle = fill;
      ctx.fillRect(0, 0, width, height);

      const gData = ctx.getImageData(0, 0, width, height).data;
      const s = Math.max(0, Math.min(1, this.strength ?? 0.85));

      for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        if (a === 0) continue;
        // Multiply blend, mixed with original by strength
        const mr = (data[i] * gData[i]) / 255;
        const mg = (data[i + 1] * gData[i + 1]) / 255;
        const mb = (data[i + 2] * gData[i + 2]) / 255;
        data[i] = data[i] * (1 - s) + mr * s;
        data[i + 1] = data[i + 1] * (1 - s) + mg * s;
        data[i + 2] = data[i + 2] * (1 - s) + mb * s;
      }
    },

    toObject() {
      return {
        ...this.callSuper('toObject'),
        from: this.from,
        to: this.to,
        kind: this.kind,
        strength: this.strength,
      };
    },
  }) as unknown as {
    new (options?: Record<string, unknown>): GradientFilterInstance;
    fromObject: (
      object: Record<string, unknown>,
      callback: (f: GradientFilterInstance) => void
    ) => void;
  };

  CraftaraGradientOverlay.fromObject = (object, callback) => {
    callback(new CraftaraGradientOverlay(object));
  };

  filters[FILTER_TYPE] = CraftaraGradientOverlay;
  // Legacy filter type from PIXKIT-era saves
  filters.PixkitGradientOverlay = CraftaraGradientOverlay;
  filterRegistered = true;
}

function makeShapeGradient(
  kind: GradientKind,
  colors: GradientColors,
  w: number,
  h: number
): fabric.Gradient {
  const width = Math.max(1, w);
  const height = Math.max(1, h);

  if (kind === 'radial') {
    return new fabric.Gradient({
      type: 'radial',
      gradientUnits: 'pixels',
      coords: {
        x1: width / 2,
        y1: height / 2,
        x2: width / 2,
        y2: height / 2,
        r1: 0,
        r2: Math.max(width, height) / 2,
      },
      colorStops: [
        { offset: 0, color: colors.from },
        { offset: 1, color: colors.to },
      ],
    });
  }

  return new fabric.Gradient({
    type: 'linear',
    gradientUnits: 'pixels',
    coords: { x1: 0, y1: 0, x2: width, y2: 0 },
    colorStops: [
      { offset: 0, color: colors.from },
      { offset: 1, color: colors.to },
    ],
  });
}

function applyGradientToImage(
  img: fabric.Image,
  kind: GradientKind,
  colors: GradientColors
): boolean {
  ensureGradientFilter();
  const filtersNs = fabric.Image?.filters as unknown as
    | Record<string, new (o?: Record<string, unknown>) => GradientFilterInstance>
    | undefined;
  const Ctor = filtersNs?.[FILTER_TYPE];
  if (!Ctor) return false;

  const rest = (img.filters || []).filter((f) => {
    const t = (f as GradientFilterInstance).type;
    const n = (f as { constructor?: { name?: string } }).constructor?.name;
    return t !== FILTER_TYPE && n !== FILTER_TYPE;
  });

  rest.push(
    new Ctor({
      from: colors.from,
      to: colors.to,
      kind,
      strength: 0.85,
    })
  );

  img.filters = rest;
  try {
    img.applyFilters();
  } catch (err) {
    console.warn('Gradient filter failed (image may be CORS-tainted)', err);
    return false;
  }
  return true;
}

function applyGradientToShape(
  obj: fabric.Object,
  kind: GradientKind,
  colors: GradientColors
): void {
  // Local (unscaled) dimensions — Fabric gradientUnits:pixels uses object space
  const w = obj.width || 100;
  const h = obj.height || 100;
  obj.set('fill', makeShapeGradient(kind, colors, w, h));
}

/**
 * Apply a gradient to the selection:
 * - shapes / text → Fabric fill gradient
 * - images (uploads, photos, framed) → pixel gradient overlay filter
 */
export function applyGradientFill(
  canvas: fabric.Canvas,
  kind: GradientKind,
  colors: GradientColors
): boolean {
  ensureGradientFilter();

  const active = canvas.getActiveObject();
  if (!active) return false;

  const targets: fabric.Object[] =
    active.type === 'activeSelection'
      ? (active as fabric.ActiveSelection).getObjects()
      : active.type === 'group'
        ? (active as fabric.Group).getObjects()
        : [active];

  let applied = false;

  targets.forEach((obj) => {
    if (obj.type === 'image') {
      if (applyGradientToImage(obj as fabric.Image, kind, colors)) {
        applied = true;
      }
      return;
    }

    if (obj.type === 'group') {
      (obj as fabric.Group).getObjects().forEach((child) => {
        if (child.type === 'image') {
          if (applyGradientToImage(child as fabric.Image, kind, colors)) {
            applied = true;
          }
        } else if (child.type !== 'group') {
          applyGradientToShape(child, kind, colors);
          applied = true;
        }
      });
      return;
    }

    applyGradientToShape(obj, kind, colors);
    applied = true;
  });

  if (applied) canvas.requestRenderAll();
  return applied;
}

export function isGradientFill(fill: unknown): boolean {
  return Boolean(
    fill && typeof fill === 'object' && 'colorStops' in (fill as object)
  );
}
